/**
 * p80-experiment-tracking.js
 * ---------------------------------------------------------------------------
 * Purpose:
 *   Fires custom analytics events for the "collection_filters_2026_07" A/B
 *   experiment, comparing collection filter UX across two published themes:
 *     - main = control    (data-p80-variant-id="control")
 *     - Surplus City [Dev - J] - Filter Buttons A v2 = treatment  (data-p80-variant-id="treatment")
 *
 * How it fits together:
 *   theme.liquid sets data-p80-experiment-id / data-p80-variant-id /
 *   data-p80-collection-handle on <html> (collection pages only)
 *     -> this script reads those attributes, pushes the GA4-named event
 *        (p80: prefix stripped) into window.dataLayer for the store's GTM
 *        container, and also calls Shopify.analytics.publish("p80:<event>",
 *        {...}) so any web pixel can receive the same payload.
 *
 *   The Custom Pixel cannot be the GTM bridge: it runs in a sandboxed iframe
 *   (no allow-same-origin), so `window` inside it has no dataLayer and
 *   `window.parent` access throws SecurityError. Forwarding to dataLayer has
 *   to happen here, in a script with real window access.
 *
 * IMPORTANT — keep in sync across both themes:
 *   This file must be byte-for-byte identical in Theme X and Theme Y.
 *   The only per-theme difference lives in theme.liquid
 *   (data-p80-variant-id="control" vs "treatment"). If you edit tracking
 *   logic in one theme, copy the same change to the other or the experiment
 *   data will no longer be comparable.
 *
 * Events published (must match the pixel's forwardEvent() list exactly):
 *   p80:experiment_impression      - once per collection page load
 *   p80:collection_filter_change   - checkbox filter checked/unchecked
 *   p80:collection_filter_clear    - "Remove All" clicked
 *   p80:collection_filter_results  - product grid re-rendered after a filter action
 *   p80:collection_product_click   - a product link inside the grid was clicked
 *
 * Each one is also delivered to GA4 under its own name
 * (experiment_impression, collection_filter_change, collection_filter_clear,
 * collection_filter_results, collection_product_click) with the same payload, via
 * gtag('event', ...). This store runs the Google tag (gtag.js) and has NO GTM
 * container, so a bare window.dataLayer.push reaches nothing.
 *
 * Selectors this script depends on (confirmed identical in both themes as of
 * 2026-07-30 — re-verify here if either theme's filter markup changes):
 *   input[name^="filter."]         - all filter checkboxes
 *   .active-facets__button-remove  - "Remove All" button
 *   #ProductGridContainer          - stable facet wrapper (MutationObserver target)
 *   #product-grid                  - product grid; it is REPLACED on every facet
 *                                    update, so it must be re-queried, never cached
 *   #ProductCountDesktop           - visible product count, desktop
 *   .mobile-facets__count          - visible product count, mobile
 *
 * Load order: place near </body> (or use `defer`), after other theme JS.
 * No page-type conditional needed around the <script> tag itself — the
 * script no-ops safely via the data-p80-experiment-id guard below on any
 * page that isn't part of the experiment.
 * ---------------------------------------------------------------------------
 */

(function () {
  const html = document.documentElement;
  const experimentId = html.dataset.p80ExperimentId;
  if (!experimentId) return; // not a collection page in the experiment

  const variantId = html.dataset.p80VariantId;
  const collectionHandle = html.dataset.p80CollectionHandle;
  // The facet form replaces #product-grid on every update, so look it up on demand.
  function currentGrid() {
    return document.querySelector('#product-grid');
  }

  // GA4/GTM event name for each published p80:<event>.
  const GA4_EVENT_NAMES = {
    'p80:experiment_impression': 'experiment_impression',
    'p80:collection_filter_change': 'collection_filter_change',
    'p80:collection_filter_clear': 'collection_filter_clear',
    'p80:collection_filter_results': 'collection_filter_results',
    'p80:collection_product_click': 'collection_product_click'
  };

  function publish(eventName, extra) {
    const payload = Object.assign(
      {
        experiment_id: experimentId,
        variant_id: variantId,
        collection_handle: collectionHandle
      },
      extra
    );

    Shopify.analytics.publish(eventName, payload);

    const ga4EventName = GA4_EVENT_NAMES[eventName] || eventName;

    // gtag.js (the Google tag) is what actually reaches GA4 here — this store has
    // no GTM container, so a bare window.dataLayer.push would go nowhere. GA4 only
    // accepts string/number parameters, so booleans are stringified and
    // null/undefined values are dropped.
    if (typeof window.gtag === 'function') {
      const params = {};
      Object.keys(payload).forEach((key) => {
        const value = payload[key];
        if (value === undefined || value === null) return;
        params[key] = typeof value === 'boolean' ? String(value) : value;
      });
      window.gtag('event', ga4EventName, params);
    }

    // Written for a future GTM container. If you ever add one, remove the gtag
    // call above or every event will be reported to GA4 twice.
    (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: ga4EventName }, payload));
  }

  function activeFilterCount() {
    // Every filter is rendered twice (desktop facets + mobile drawer), so count
    // distinct names — counting checked inputs reports double.
    const names = new Set();
    document.querySelectorAll('input[name^="filter."]:checked').forEach((input) => names.add(input.name));
    return names.size;
  }

  // Parses "91 of 91 products" -> 91. Falls back to mobile count if desktop node is hidden/absent.
  function getResultCount() {
    const desktop = document.querySelector('#ProductCountDesktop');
    const mobile = document.querySelector('.mobile-facets__count');
    const source = (desktop?.offsetParent !== null ? desktop : mobile) || desktop || mobile;
    if (!source) return null;
    const text = source.textContent;
    const ofMatch = text.match(/(\d+)\s+of\s+(\d+)/i);
    if (ofMatch) return parseInt(ofMatch[1], 10);
    // Unfiltered, the count reads "472 products" rather than "472 of 472 products".
    const totalMatch = text.match(/(\d+)\s+products?/i);
    return totalMatch ? parseInt(totalMatch[1], 10) : null;
  }

  function resultBucket(count) {
    if (count === null) return 'unknown';
    if (count === 0) return '0';
    if (count <= 10) return '1-10';
    if (count <= 50) return '11-50';
    return '50+';
  }

  // 1. Impression — once per page load
  publish('p80:experiment_impression', {});

  // 2. Filter change (checkbox add/remove, including quick-filter buttons
  //    since those check the box programmatically and dispatch change)
  document.addEventListener('change', (event) => {
    const input = event.target.closest('input[name^="filter."]');
    if (!input) return;
    publish('p80:collection_filter_change', {
      filter_action: input.checked ? 'add' : 'remove',
      filter_id: input.name,
      filter_value: input.value,
      active_filter_count: activeFilterCount()
    });
  });

  // 3. Clear all
  document.addEventListener('click', (event) => {
    const clearAll = event.target.closest('.active-facets__button-remove');
    if (!clearAll) return;
    publish('p80:collection_filter_clear', {
      active_filter_count_before_clear: activeFilterCount()
    });
  });

  // 4. Results updated — fires after the AJAX-rerendered grid settles.
  //    Observing #product-grid itself only works until the first facet update
  //    (which detaches that node), so observe the wrapper that survives it.
  const resultsRoot = document.querySelector('#ProductGridContainer');
  if (resultsRoot) {
    let debounce;
    let lastGrid;
    let lastCount;
    new MutationObserver(() => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        const grid = currentGrid();
        const resultCount = getResultCount();
        // The wrapper can settle in more than one wave; report each render once.
        if (grid === lastGrid && resultCount === lastCount) return;
        lastGrid = grid;
        lastCount = resultCount;
        publish('p80:collection_filter_results', {
          result_count: resultCount,
          result_bucket: resultBucket(resultCount),
          filtered: activeFilterCount() > 0
        });
      }, 150);
    }).observe(resultsRoot, {childList: true, subtree: true});
  }

  // 5. Product click
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="/products/"]');
    const grid = currentGrid();
    if (!link || !grid?.contains(link)) return;
    publish('p80:collection_product_click', {
      product_path: new URL(link.href).pathname
    });
  });
})();
